import type { ScenarioAttributeNode } from '../../../features/scenarios/api';
import type { HierarchicalOption } from '../../common/HierarchicalSelect';

// シナリオ全体のターゲット (属性ツリー scenario_basic > target_gender / target_age > option)。
// 選択肢と指示本文は DB が正本 (docs/features/scenario-target-audience.md)。空文字は指定なし (プロンプト追記なし)。
// ツリーに field が無い環境 (migration 未適用) では選択肢が空になり、項目を出さない (撮影方法・方言と同じ)。
export const TARGET_GENDER_FIELD_KEY = 'target_gender';
export const TARGET_AGE_FIELD_KEY = 'target_age';

/** payload / generation_metadata / localStorage の値を option key に正規化する ('none'・文字列以外は指定なし)。 */
export function normalizeTargetKey(raw: unknown): string {
  const key = typeof raw === 'string' ? raw.trim() : '';
  return key === 'none' ? '' : key;
}

function findField(items: ScenarioAttributeNode[], key: string): ScenarioAttributeNode | undefined {
  for (const node of items) {
    if (node.key === key && node.node_type === 'group') return node;
    const found = findField(node.children ?? [], key);
    if (found) return found;
  }
}

function toOptions(nodes: ScenarioAttributeNode[]): HierarchicalOption[] {
  return nodes.flatMap((node): HierarchicalOption[] => {
    if (node.node_type === 'group') {
      const children = toOptions(node.children ?? []);
      return children.length ? [{ id: node.id, key: node.key, label: node.label, type: 'group', children }] : [];
    }
    if (node.node_type !== 'option') return [];
    return [{ id: node.id, key: node.key, label: node.label, type: 'option' }];
  });
}

const placementOf = (field?: ScenarioAttributeNode): 'primary' | 'advanced' =>
  (field?.placement === 'primary' ? 'primary' : 'advanced');

export function deriveTargetAudienceOptions(items?: ScenarioAttributeNode[]) {
  const genderField = findField(items ?? [], TARGET_GENDER_FIELD_KEY);
  const ageField = findField(items ?? [], TARGET_AGE_FIELD_KEY);
  return {
    targetGenderOptions: toOptions(genderField?.children ?? []),
    targetGenderFieldPlacement: placementOf(genderField),
    targetAgeOptions: toOptions(ageField?.children ?? []),
    targetAgeFieldPlacement: placementOf(ageField),
  };
}

function findLabel(options: HierarchicalOption[], key: string): string | undefined {
  for (const option of options) {
    if (option.type === 'option' && option.key === key) return option.label;
    const label = findLabel(option.children ?? [], key);
    if (label !== undefined) return label;
  }
}

/**
 * 設定サマリーの表記。年代→性別の順に続けて「20代女性」。性別のラベルが 2 文字より長いとき (「男女両方」) は
 * 「20代・男女両方」と区切る。片方だけならそのラベル。ツリーに無い値 (指定なし含む) は出さない。
 */
export function formatTargetAudienceSummary(
  genderOptions: HierarchicalOption[], gender: string,
  ageOptions: HierarchicalOption[], age: string,
): string | undefined {
  const genderLabel = gender ? findLabel(genderOptions, gender) : undefined;
  const ageLabel = age ? findLabel(ageOptions, age) : undefined;
  if (ageLabel && genderLabel) return genderLabel.length <= 2 ? `${ageLabel}${genderLabel}` : `${ageLabel}・${genderLabel}`;
  return ageLabel ?? genderLabel;
}
